/* Nueve Sterling motion engine.
   NS.motion.register(el, factory): factory(el, tier) runs on first approach (200px), may return
   { play(), pause(), destroy() }. pause/play follow viewport + tab visibility; destroy fires when
   the element is more than 2 viewports away. */
(() => {
  const NS = (window.NS = window.NS || {});
  const root = document.documentElement;
  const mqReduce = matchMedia('(prefers-reduced-motion: reduce)');
  const reduced = mqReduce.matches || !root.classList.contains('js-motion');
  const lowPower = matchMedia('(max-width: 767px)').matches || (navigator.deviceMemory && navigator.deviceMemory <= 4) || (navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 4);
  const finePointer = matchMedia('(hover: hover) and (pointer: fine)').matches;
  const tier = (NS.tier = { reduced, low: !!lowPower, fine: finePointer, dpr: Math.min(devicePixelRatio || 1, lowPower ? 1.5 : 2) });

  /* ---------- registry ---------- */
  const entries = new Map();
  const near = new IntersectionObserver((list) => {
    for (const e of list) {
      const rec = entries.get(e.target); if (!rec) continue;
      rec.visible = e.isIntersecting;
      if (e.isIntersecting) {
        if (!rec.api) { rec.api = rec.factory(e.target, tier) || {}; }
        if (!document.hidden) rec.api.play && rec.api.play();
      } else rec.api && rec.api.pause && rec.api.pause();
    }
  }, { rootMargin: '200px 0px' });
  const far = new IntersectionObserver((list) => {
    for (const e of list) {
      const rec = entries.get(e.target);
      if (rec && !e.isIntersecting && rec.api) { rec.api.destroy && rec.api.destroy(); rec.api = null; }
    }
  }, { rootMargin: '200% 0px' });

  NS.motion = {
    register(el, factory, { allowReduced = false } = {}) {
      if (!el || entries.has(el) || (reduced && !allowReduced)) return;
      if (el.closest('[data-motion-level="none"]') && !allowReduced) return;
      entries.set(el, { factory, api: null, visible: false });
      near.observe(el); far.observe(el);
    },
  };
  document.addEventListener('visibilitychange', () => {
    entries.forEach((r) => { if (!r.api) return; document.hidden ? r.api.pause && r.api.pause() : r.visible && r.api.play && r.api.play(); });
  });

  if (reduced) return initNonMotion();

  /* ---------- reveals ---------- */
  // A target fully hidden by its own clip-path never reports an intersection, so clip reveals are watched via their parent.
  const REVEAL = '[data-reveal], .filigree-draw, .silver-sweep, [data-inview], .papel.is-armed, [data-split]';
  const CLIP_REVEAL = '.silver-sweep, [data-reveal="mask"]';
  const onReveal = (list) => {
    for (const e of list) if (e.isIntersecting) {
      const t = e.target;
      if (t._nsClip) t._nsClip.forEach((c) => c.classList.add('is-in'));
      if (!t._nsClip || t.matches(REVEAL)) t.classList.add('is-in');
      revealIO.unobserve(t); clipIO.unobserve(t);
    }
  };
  const revealIO = new IntersectionObserver(onReveal, { threshold: 0.15, rootMargin: '0px 0px -8% 0px' });
  // Parents can be far taller than the viewport (a long blog article), so a ratio threshold may never be met: fire on any overlap.
  const clipIO = new IntersectionObserver(onReveal, { threshold: 0, rootMargin: '0px 0px -8% 0px' });
  const observeReveal = (el) => {
    if (!el.matches(CLIP_REVEAL)) return revealIO.observe(el);
    const p = el.parentElement;
    (p._nsClip ||= []).push(el);
    clipIO.observe(p);
  };

  function splitWords(el) {
    if (el.dataset.splitDone || el.children.length && !el.querySelector(':scope > .u-mark')) return;
    let w = 0;
    const walk = (node) => {
      [...node.childNodes].forEach((n) => {
        if (n.nodeType === 3) {
          const frag = document.createDocumentFragment();
          n.textContent.split(/(\s+)/).forEach((part) => {
            if (!part) return;
            if (/^\s+$/.test(part)) return frag.append(part);
            const s = document.createElement('span'); s.className = 'split-word'; s.style.setProperty('--w', w++); s.textContent = part; frag.append(s);
          });
          n.replaceWith(frag);
        } else if (n.nodeType === 1 && !n.matches('svg')) walk(n);
      });
    };
    walk(el);
    el.dataset.splitDone = '1';
  }

  function scan(scope = document) {
    scope.querySelectorAll('[data-split]').forEach((el) => { splitWords(el); revealIO.observe(el); });
    scope.querySelectorAll('[data-stagger]').forEach((group) => {
      [...group.children].forEach((c, i) => { if (!c.style.getPropertyValue('--i')) c.style.setProperty('--i', Math.min(i, 6)); });
    });
    scope.querySelectorAll('[data-reveal], .filigree-draw, .silver-sweep, [data-inview], .papel.is-armed').forEach(observeReveal);
    scope.querySelectorAll('.filigree-draw path').forEach((p, i) => { p.setAttribute('pathLength', '1'); });
    scope.querySelectorAll('.draw-icon').forEach((svg) => svg.querySelectorAll('path, circle, rect, line, polyline').forEach((p) => p.setAttribute('pathLength', '1')));
    scope.querySelectorAll('.magnetic').forEach(magnetic);
    scope.querySelectorAll('[data-glint]').forEach(glint);
    scope.querySelectorAll('[data-parallax]').forEach(parallax);
    scope.querySelectorAll('[data-scrub-draw]').forEach(scrubDraw);
    // silver wire catching light: slide the gradient along the stroke while visible
    scope.querySelectorAll('linearGradient[id^="fg-"]').forEach((grad) => {
      const svg = grad.closest('svg');
      NS.motion.register(svg, () => {
        if (!window.gsap || tier.low) return {}; // infinite tween keeps the ticker hot on phones
        const tw = gsap.fromTo(grad, { attr: { gradientTransform: 'translate(-140 0)' } }, { attr: { gradientTransform: 'translate(140 0)' }, duration: 6, ease: 'sine.inOut', yoyo: true, repeat: -1, paused: true });
        return { play: () => tw.play(), pause: () => tw.pause(), destroy: () => tw.kill() };
      });
    });
  }
  NS.motion.scan = scan;

  /* ---------- magnetic buttons ---------- */
  function magnetic(el) {
    if (!tier.fine || el.dataset.mag) return; el.dataset.mag = 1;
    const strength = 0.28;
    el.addEventListener('pointermove', (e) => {
      const r = el.getBoundingClientRect();
      el.style.setProperty('--mag-x', ((e.clientX - r.left - r.width / 2) * strength).toFixed(1) + 'px');
      el.style.setProperty('--mag-y', ((e.clientY - r.top - r.height / 2) * strength).toFixed(1) + 'px');
    });
    el.addEventListener('pointerleave', () => { el.style.setProperty('--mag-x', '0px'); el.style.setProperty('--mag-y', '0px'); });
  }

  /* ---------- cursor-lit silver glint (or autonomous drift on touch) ---------- */
  function glint(el) {
    if (el.dataset.glintOn) return; el.dataset.glintOn = 1;
    if (tier.fine) {
      el.addEventListener('pointermove', (e) => {
        const r = el.getBoundingClientRect();
        el.style.setProperty('--mx', ((e.clientX - r.left) / r.width * 100).toFixed(1) + '%');
        el.style.setProperty('--my', ((e.clientY - r.top) / r.height * 100).toFixed(1) + '%');
      }, { passive: true });
    }
  }

  /* ---------- GSAP-powered: parallax + scrubbed filigree dividers ---------- */
  const gsapReady = () => window.gsap && window.ScrollTrigger;
  function parallax(el) {
    if (!gsapReady() || el.dataset.px) return; el.dataset.px = 1;
    const depth = parseFloat(el.dataset.parallax || '0.15') * (tier.low ? 0.5 : 1);
    gsap.fromTo(el, { yPercent: -depth * 50 }, { yPercent: depth * 50, ease: 'none', scrollTrigger: { trigger: el.closest('section, .shopify-section') || el, start: 'top bottom', end: 'bottom top', scrub: true } });
  }
  function scrubDraw(svg) {
    if (!gsapReady() || svg.dataset.sd) return; svg.dataset.sd = 1;
    const paths = svg.querySelectorAll('path');
    paths.forEach((p) => { p.setAttribute('pathLength', '1'); p.style.strokeDasharray = 1; p.style.strokeDashoffset = 1; });
    gsap.to(paths, { strokeDashoffset: 0, ease: 'none', stagger: 0.08, scrollTrigger: { trigger: svg, start: 'top 92%', end: 'top 45%', scrub: 0.6 } });
  }

  /* ---------- smooth scroll ---------- */
  function lenis() {
    if (!window.Lenis || root.dataset.smooth !== 'true' || !tier.fine || tier.low) return; // touch/low-power: native scroll, no per-frame loop
    const l = (NS.lenis = new Lenis({ duration: 1.1, smoothWheel: true, wheelMultiplier: 0.95 }));
    if (gsapReady()) { l.on('scroll', ScrollTrigger.update); gsap.ticker.add((t) => l.raf(t * 1000)); gsap.ticker.lagSmoothing(0); }
    else { const raf = (t) => { l.raf(t); requestAnimationFrame(raf); }; requestAnimationFrame(raf); }
    // pause smooth scroll while drawers/modals lock the page
    new MutationObserver(() => (root.classList.contains('scroll-lock') ? l.stop() : l.start())).observe(root, { attributes: true, attributeFilter: ['class'] });
  }

  function init() {
    if (gsapReady()) gsap.registerPlugin(ScrollTrigger);
    scan();
    lenis();
    // Sections re-rendered by the editor or the Section Rendering API
    document.addEventListener('shopify:section:load', (e) => scan(e.target));
    document.addEventListener('ns:dom-updated', (e) => scan(e.detail instanceof Element ? e.detail : document));
  }
  function initNonMotion() {
    NS.motion.scan = () => {};
  }
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', init) : init();
})();
